import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import classes from '../../../pipeline.module.scss';
import Api from '../../../../../../../api/api';
import Table from '../../../../../../_common/Tables/Table';
import {
  Card, LineChart, SectionHead, StatsCards,
} from '../../PipelineComponents';
import { formattedCount, formattedPercent } from '../../../pipelineHelpers';

const prescribersTableHeader = [
  { name: 'Prescriber', path: 'name' },
  { name: 'Members', path: 'members' },
  { name: 'Scripts', path: 'scripts' },
  { name: '90-day rate', path: 'rate' },
  { name: 'Opportunity', path: 'opportunity' },
];

const DRUG_CLASS_TIPS = {
  Generic: {
    text: 'The share of generic dispensing on a long supply.',
    steps: ['90-day and 100-day fills divided by every generic fill.'],
    note: 'Usually close to the ceiling. The less there is left to win here, the more the brand figure is the'
      + ' whole opportunity.',
  },
  Brand: {
    text: 'The share of brand dispensing on a long supply.',
    steps: ['90-day and 100-day fills divided by every brand fill.'],
    note: 'Lines up with section 01, where brand members lose far more days than generic members. Short brand'
      + ' supplies are where the therapy loss is being created.',
  },
  Immunotherapy: {
    text: 'The share of immunotherapy dispensing on a long supply.',
    steps: ['90-day and 100-day fills divided by every immunotherapy fill.'],
    note: 'Expect a low rate here. Plan limits and cold-chain handling often cap these at a short supply, so'
      + ' a small figure is not automatically an opportunity to work.',
  },
};

const FillRates = ({ section, params }) => {
  const navigate = useNavigate();

  const firstLoad = useRef(true);

  const [summary, setSummary] = useState({});
  const [loading, loadingToggle] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        loadingToggle(true);

        const { data } = await Api.getFillRate90Summary(params);

        setSummary(data?.data || {});

        loadingToggle(false);
        firstLoad.current = false;
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [params]);

  const { cards, drugClasses, prescribers } = useMemo(() => {
    const kpis = summary?.kpis;

    return {
      cards: [
        {
          label: 'Total fills',
          value: formattedCount(kpis?.totalFills),
          tooltip: {
            text: 'Every prescription dispensed in the period.',
            steps: ['Counts fills, not members.'],
            note: 'The base for the fill rate beside it. Section 02 counts fills for the same period'
              + ' differently, so the two totals will not match.',
            warn: true,
          },
        },
        {
          label: '90-day+ fill rate',
          value: formattedPercent(kpis?.fillRate90),
          tooltip: {
            text: 'The share of dispensing on a long supply.',
            steps: [
              'Long fills divided by every fill in the period.',
              '90-day and 100-day both count as long.',
            ],
            note: 'Read it per prescriber rather than as one number. A single low-volume prescriber will not'
              + ' move it, but a busy one will.',
          },
        },
        {
          label: 'Fills at 90/100 days',
          value: formattedCount(kpis?.fills90Plus),
          tooltip: {
            text: 'Prescriptions dispensed for 90 days or more.',
            steps: ['90-day and 100-day supplies counted together.'],
            note: 'Every fill here is a member with three or four pickups a year instead of twelve — three or'
              + ' four chances to lapse instead of twelve.',
          },
        },
        {
          label: 'Fills under 90 days',
          value: formattedCount(kpis?.fillsUnder90),
          tooltip: {
            text: 'Prescriptions dispensed for less than 90 days.',
            steps: ['Everything from a short course upward.'],
            note: 'Section 02 breaks this down and narrows it to the members genuinely worth converting. Not'
              + ' all of it should move — short courses belong on a short supply.',
          },
        },
        {
          label: 'Prescribers below 40%',
          value: formattedCount(kpis?.prescribersBelow40),
          tooltip: {
            text: 'Prescribers writing mostly short supplies.',
            steps: ['Measured across everything they wrote in the period.'],
            note: 'The outreach list. A small number of prescribers usually account for most of the short'
              + ' dispensing, which is what makes this worth working.',
          },
        },
      ],

      drugClasses: (summary?.byDrugClass || []).map((d) => ({
        label: d.brand || '-',
        value: Number(d.fillRate90) || 0,
        caption: formattedPercent(d.fillRate90),
        desc: `${formattedCount(d.members)} members · ${formattedCount(d.totalFills)} fills`,
        alt: Number(d.fillRate90) < 60,
        tooltip: DRUG_CLASS_TIPS[d.brand],
      })),

      prescribers: (summary?.prescriberOutliers || []).map((d) => ({
        id: d.physicianId,
        name: d.prescriber,
        members: formattedCount(d.members),
        scripts: formattedCount(d.scripts),
        rate: formattedPercent(d.fillRate90),
        opportunity: formattedCount(d.opportunity),
      })),
    };
  }, [summary]);

  return (
    <>
      <SectionHead section={section} />

      <StatsCards
        cards={cards}
        loading={loading}
        firstLoad={firstLoad.current}
      />

      <div className={`${classes.row} ${classes.row_2}`}>
        <Card
          title="90-day rate by drug class"
          caption="Share of fills at 90 or 100 days"
          loading={loading}
          firstLoad={firstLoad.current}
          isEmpty={!drugClasses.length}
        >
          <LineChart
            data={drugClasses}
            scale="percent"
          />
        </Card>

        <Card
          title="Prescriber outliers"
          caption="Lowest 90-day rates by volume - the outreach list"
          loading={loading}
          firstLoad={firstLoad.current}
          isEmpty={!prescribers.length}
        >
          <div className={`${classes.pipeline_table} ${classes.bordered}`}>
            <Table
              header={prescribersTableHeader}
              data={prescribers}
              className={classes.clickable_row}
              onClick={(physicianId) => navigate(`/physicians/${physicianId}`)}
            />
          </div>

          <p className={classes.note}>
            <b>Opportunity</b>
            {' '}
            = short fills on drugs the member&apos;s plan allows at 90/100 days - the count that
            would move if the prescriber rewrote.
          </p>
        </Card>
      </div>

      {/* <FillRateByMonthCard params={params} /> */}
    </>
  );
};

FillRates.propTypes = {
  section: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
};

export default FillRates;
